import CalendarIcon from "assets/images/Calendar";
import ClockIcon from "assets/images/Clock";
import locationPinIcon from "assets/images/location-pin-small.svg";
import dropDownIcon from "assets/images/select-dropdown-icon.svg";
import classNames from "classnames";
import {format, isSameDay, parse} from "date-fns";
import {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {scheduleAppointment as strings} from "strings";

import Button from "components/Button";
import ConfirmAppointmentModal from "components/ConfirmAppointmentModal";
import DatePickerModal from "components/DatePickerModal";
import Divider from "components/Divider";
import MobileNavigationHeader from "components/MobileNavigationHeader";
import PageTitle from "components/PageTitle";
import TimePickerModal from "components/TimePickerModal";

import * as models from "liberty-core/api/models";
import {
  useAvailableAppointmentDates,
  useGetAppointmentQuestions,
} from "liberty-core/api/react-query/appointment";

import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./ScheduleAppointmentPage.module.css";

function getFormattedTime(time: models.TimeString) {
  const parsedDate = parse(time, "HH:mm", new Date());

  return format(parsedDate, "p");
}

type LocationState = {
  guestInfo?: models.AppointmentGuestInfo;
  selectedPatientId: string | null;
  campaignName: string;
  options: models.AppointmentCampaignOption[];
  apptCampaigns: models.AppointmentCampaign[];
  appointmentType: string;
  selectedCampaignId: string;
  selectedCampaignOptionId: string;
  startDateTime?: string;
  insuranceInfo?: {frontImage: File; backImage: File};
};

export default function ScheduleAppointmentPage() {
  const [pharmacy] = useCurrentPharmacy();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const guestInfo = locationState.guestInfo;
  const selectedPatientId = locationState.selectedPatientId;
  const campaignName = locationState.campaignName;
  const options = locationState.options;
  const appointmentType = locationState.appointmentType;
  const apptCampaigns = locationState.apptCampaigns;
  const selectedCampaignId = locationState.selectedCampaignId;
  const selectedCampaignOptionId = locationState.selectedCampaignOptionId;
  const startDateTimePrev = locationState.startDateTime;
  const insuranceInfo = locationState.insuranceInfo;

  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);
  const [isTimePickerVisible, setIsTimePickerVisible] = useState(false);
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] =
    useState(false);

  const [date, setDate] = useState<Date | undefined>(
    startDateTimePrev ? new Date(startDateTimePrev) : undefined,
  );
  const [time, setTime] = useState<models.TimeString | undefined>(
    startDateTimePrev
      ? (format(new Date(startDateTimePrev), "kk:mm") as models.TimeString)
      : undefined,
  );
  const [startDateTime, setStartDateTime] = useState(startDateTimePrev ?? "");
  const navigate = useNavigate();

  const {data: appointmentDates, isLoading: isLoadingAppointmentDates} =
    useAvailableAppointmentDates(pharmacy?.id ?? 0, selectedCampaignOptionId);
  const [appointmentTimes, setAppointmentTimes] = useState<
    models.AppointmentDateTimeSlot[]
  >(
    startDateTimePrev
      ? appointmentDates?.timeSlotsList.filter((ts) =>
          isSameDay(new Date(startDateTimePrev), new Date(ts.startTime)),
        ) ?? []
      : [],
  );

  const {data: appointmentQuestions, isLoading: isAppointmentQuestionsLoading} =
    useGetAppointmentQuestions(
      {
        pharmacyId: pharmacy?.id ?? 0,
        appointmentOptionId: selectedCampaignOptionId,
        startTime: time ?? "",
      },
      {enabled: !!time},
    );

  useEffect(() => {
    if (!guestInfo && !selectedPatientId) {
      navigate("/appointments", {replace: true});
    }
  }, [navigate, guestInfo, selectedPatientId]);

  const onBack = () => {
    const selectedCampaign = selectedCampaignId
      ? apptCampaigns.find((c) => c.id === selectedCampaignId)
      : undefined;
    const hasMoreThanOneOption =
      selectedCampaign?.appointmentOptions?.length &&
      selectedCampaign?.appointmentOptions?.length > 1;

    if (hasMoreThanOneOption) {
      navigate("/appointments/select-option", {
        state: {
          apptCampaigns: apptCampaigns ?? [],
          guestInfo,
          selectedPatientId,
          appointmentType,
          selectedCampaignId,
          campaignName,
          options,
          selectedCampaignOptionId,
          insuranceInfo,
        },
      });
    } else {
      navigate("/appointments/select-campaign", {
        state: {
          apptCampaigns: apptCampaigns ?? [],
          guestInfo,
          selectedPatientId,
          appointmentType,
          selectedCampaignId,
          insuranceInfo,
        },
      });
    }
  };

  return (
    <div className={styles.container}>
      <MobileNavigationHeader onBack={onBack} alwaysShown />
      <PageTitle className={styles.title}>{strings.title}</PageTitle>
      <div className={styles.location}>
        <img src={locationPinIcon} alt="" />
        <span className={styles.subtitle}>{pharmacy?.name}</span>
      </div>
      <Divider className={styles.divider} />
      <div className={styles.apptType}>{campaignName}</div>
      <Divider className={classNames(styles.divider, styles.dividerMobile)} />
      <button
        className={styles.selectionRow}
        onClick={() => {
          setIsDatePickerVisible(true);
        }}
      >
        <div className={styles.selectionRowLeft}>
          <CalendarIcon />
          <div className={styles.selectionRowLabel}>{strings.date}</div>
        </div>
        <div className={styles.dropDown}>
          <div className={styles.selectedText}>
            {date ? date.toLocaleDateString() : strings.selectDate}
          </div>
          <img src={dropDownIcon} alt="" />
        </div>
      </button>
      <DatePickerModal
        visible={isDatePickerVisible}
        onClickConfirm={(confirmedDate) => {
          setDate(confirmedDate);
          setTime(undefined);
          setAppointmentTimes(
            appointmentDates?.timeSlotsList.filter((ts) =>
              isSameDay(confirmedDate, new Date(ts.startTime)),
            ) ?? [],
          );
          setIsDatePickerVisible(false);
        }}
        onClickCancel={() => setIsDatePickerVisible(false)}
        availableDates={appointmentDates?.timeSlotsList.map(
          (t) => new Date(t.startTime),
        )}
        datePicked={date}
        isLoading={isLoadingAppointmentDates}
      />
      <Divider className={classNames(styles.divider, styles.dividerWeb)} />
      <button
        className={styles.selectionRow}
        onClick={() => {
          setIsTimePickerVisible(true);
        }}
        disabled={!date}
      >
        <div className={styles.selectionRowLeft}>
          <ClockIcon color={date ? undefined : "#9f9f9f"} />
          <div
            className={
              date
                ? styles.selectionRowLabel
                : classNames(styles.selectionRowLabel, styles.disabledText)
            }
          >
            {strings.time}
          </div>
        </div>
        <div
          className={
            date
              ? styles.dropDown
              : classNames(styles.dropDown, styles.disabledText)
          }
        >
          <div className={styles.selectedText}>
            {time ? getFormattedTime(time) : strings.selectTime}
          </div>
          <img src={dropDownIcon} alt="" />
        </div>
      </button>
      <TimePickerModal
        visible={isTimePickerVisible}
        onClickConfirm={(confirmedTime) => {
          setTime(confirmedTime);
          setStartDateTime(
            appointmentTimes.filter(
              (t) =>
                format(new Date(t.startTime ?? ""), "kk:mm") === confirmedTime,
            )[0].startTime,
          );
          setIsTimePickerVisible(false);
        }}
        onClickCancel={() => setIsTimePickerVisible(false)}
        allTimes={
          appointmentTimes.map((t) =>
            format(new Date(t.startTime ?? ""), "kk:mm"),
          ) as models.TimeString[]
        }
        availableTimes={
          appointmentTimes
            .filter((t) => t.numAvailableSlots > 0)
            .map((t) =>
              format(new Date(t.startTime ?? ""), "kk:mm"),
            ) as models.TimeString[]
        }
        timePicked={time}
      />
      <Divider className={classNames(styles.divider, styles.dividerWeb)} />
      <Button
        className={styles.continue}
        disabled={!date || !time || isAppointmentQuestionsLoading}
        onClick={() => {
          if (appointmentQuestions?.resultQuestions.length) {
            navigate("/appointments/questionnaire", {
              state: {
                campaignName,
                options,
                selectedPatientId,
                guestInfo,
                apptCampaigns,
                appointmentType,
                selectedCampaignId,
                selectedCampaignOptionId,
                correlationId: appointmentQuestions.correlationId ?? "",
                resultQuestions: appointmentQuestions.resultQuestions ?? [],
                startDateTime,
                insuranceInfo,
              },
            });
          } else {
            setIsConfirmationModalVisible(true);
          }
        }}
      >
        {strings.continue}
      </Button>
      <ConfirmAppointmentModal
        visible={isConfirmationModalVisible}
        onClose={() => setIsConfirmationModalVisible(false)}
        guestInfo={guestInfo}
        selectedPatientId={selectedPatientId}
        campaignName={campaignName}
        selectedCampaignOptionId={selectedCampaignOptionId}
        correlationId={appointmentQuestions?.correlationId ?? ""}
        startDateTime={startDateTime}
        responses={null}
        insuranceInfo={insuranceInfo}
      />
    </div>
  );
}
